import React, { memo, useEffect, useRef, useState } from "react";
import { Accordion, Card, Col, Row } from "react-bootstrap";
import { ROLES } from "shared/config";
import { usePrevious } from "shared/hooks";
import "./CostLinesAccordion.styles.css";
import { useTranslation } from "react-i18next";

function CostLinesAccordion({ children, defaultAllOpen = false, userRole }) {
  const bottomRef = useRef(null);
  const { t } = useTranslation("Request");

  const [activeAccordionItems, setActiveAccordionItems] = useState(
    () =>
      new Set(
        defaultAllOpen ? children?.map((_, idx) => idx.toString()) : ["0"]
      )
  );

  const handleAccordionClick = (idx) => {
    const newSet = new Set(activeAccordionItems);
    if (activeAccordionItems.has(idx)) {
      newSet.delete(idx);
      setActiveAccordionItems(newSet);
    } else {
      newSet.add(idx);
      setActiveAccordionItems(newSet);
    }
  };

  const prevCount = usePrevious(React.Children.count(children));

  useEffect(() => {
    const currentCount = React.Children.count(children);
    let timeout;

    if (prevCount && prevCount < currentCount) {
      clearTimeout(timeout);

      const addedItems = Array(currentCount - prevCount)
        .fill(null)
        .map((_, idx) => (prevCount + idx).toString());

      const newSet = new Set([...activeAccordionItems, ...addedItems]);

      setActiveAccordionItems(newSet);
      timeout = setTimeout(
        () =>
          bottomRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          }),
        400
      );
    }
  }, [children]);

  return (
    <>
      {userRole === ROLES.ACCOUNTS_PAYABLE && (
        <Card
          className="details-container__card pb-0 pt-4"
          style={{ marginBottom: "-0.5rem" }}
        >
          <Row lg={2} className="mb-3">
            <Col md>
              <h2 className="fs-3 mt-3 mb-4">{t("cost_lines")}</h2>
            </Col>
          </Row>
        </Card>
      )}
      <Accordion activeKey={[...activeAccordionItems]}>
        {React.Children.map(children, (child, idx) => (
          <Accordion.Item eventKey={idx.toString()} key={child.id}>
            <Accordion.Button
              as="div"
              className="accordion-item-header"
              onClick={() => handleAccordionClick(idx.toString())}
            >
              <div className="ms-3">#{idx + 1}</div>
              {child.props.accordionHeader}
            </Accordion.Button>
            <Accordion.Body>{React.cloneElement(child)}</Accordion.Body>
          </Accordion.Item>
        ))}
        <span ref={bottomRef} />
      </Accordion>
    </>
  );
}

export default memo(CostLinesAccordion);
